import { useCallback, useMemo } from "react"
import { toast } from "react-toastify"
import dayjs from "dayjs"

import { useUpdateEnterpriseMutation } from "store/api/enterpriseApi"
import { useLazyGetUserProfileQuery } from "store/api/profileApi"
import { useAppDispatch } from "store/hooks"
import { setUserProfile } from "store/userSlise"
import { StorageKeys } from "types/enums/StorageKeys"

import getErrorMessage from "utils/getErrorMessage"
import { setStorageItem } from "utils/localStorage"
import { useUserProfile } from "hooks/useUserProfile"

import {
  AdditionalValues,
  getEnterpriseAdditionalInitialState,
  getEnterpriseProfileInitialState,
  prepareEnterpriseAccountFormData,
  ProfileValues,
} from "./shared"

export const useUpdateEnterpriseAccount = () => {
  const dispatch = useAppDispatch()
  const { userProfile, enterpriseCountry } = useUserProfile()
  const enterprise = userProfile?.enterprise
  const [triggerUpdateEnterprise, { isLoading }] = useUpdateEnterpriseMutation()
  const [triggerGetProfile] = useLazyGetUserProfileQuery()

  const initialProfileState = useMemo(() => {
    return getEnterpriseProfileInitialState(enterprise, enterpriseCountry)
  }, [enterprise, enterpriseCountry])

  const initialAdditionalState = useMemo(() => {
    return getEnterpriseAdditionalInitialState(enterprise)
  }, [enterprise])

  const updateEnterprise = useCallback(
    async ({
      profileValues,
      additionalValues,
    }: {
      profileValues?: ProfileValues | undefined
      additionalValues?: AdditionalValues | undefined
    }) => {
      if (!enterprise) return false

      const formData = prepareEnterpriseAccountFormData({
        enterprise,
        profileValues: profileValues || initialProfileState,
        additionalValues: additionalValues || initialAdditionalState,
      })

      if (Array.from(formData.keys()).length === 0) {
        toast.info("No changes to save.")
        return false
      }

      try {
        const updateResult = await triggerUpdateEnterprise({ id: enterprise.id, body: formData })
        const updateError = getErrorMessage(updateResult.error, "description")

        if (updateError) {
          toast.error(updateError)
          return false
        }

        const profileResult = await triggerGetProfile()
        const profileError = getErrorMessage(profileResult.error)

        if (profileError) {
          toast.success("Enterprise account has been successfully updated!")
          return true
        }

        const nextProfile = profileResult.data?.data

        if (nextProfile) {
          const data = { ...nextProfile, lastSync: dayjs() }
          setStorageItem(StorageKeys.UserProfile, data)
          dispatch(setUserProfile(data))
        }

        toast.success("Enterprise account has been successfully updated!")
        return true
      } catch (error) {
        toast.error("Something went wrong while updating enterprise account")
        console.error(error)
        return false
      }
    },
    [
      dispatch,
      enterprise,
      enterpriseCountry,
      initialAdditionalState,
      initialProfileState,
      triggerGetProfile,
      triggerUpdateEnterprise,
    ],
  )

  return { enterprise, isLoading, updateEnterprise }
}
